import React, { useState, useContext, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import useApi from '../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../utils/secure-route/AuthContext';
import { useToastHandler } from '../../../components/common/ToastHandler';
import { ToastContainer } from 'react-toastify';
import './CameraStream.css';

const STATUS_COLORS = {
  active: { bg: 'rgba(34,197,94,0.1)', text: '#16a34a', dot: '#16a34a' },
  inactive: { bg: 'rgba(220,38,38,0.1)', text: '#dc2626', dot: '#dc2626' },
  default: { bg: 'rgba(148,163,184,0.1)', text: '#94a3b8', dot: '#94a3b8' },
};
const getStatus = (s) => STATUS_COLORS[s] || STATUS_COLORS.default;

function CameraStream() {
  const { user } = useContext(AuthContext);
  const { Get, Post, getAPI } = useApi();
  const { handleToast } = useToastHandler();

  const [cameras, setCameras] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [cameraDetail, setCameraDetail] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamLoading, setStreamLoading] = useState(false);
  const [streamConnecting, setStreamConnecting] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [clock, setClock] = useState('');

  const cameraWsRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const hlsRef = useRef(null);
  const videoZoneRef = useRef(null);
  const streamAbortRef = useRef(null);
  const streamStoppedRef = useRef(false);
  const streamRunningRef = useRef(false);
  const hlsStartingRef = useRef(false);
  const wsReconnectRef = useRef(null);
  const wsStoppedRef = useRef(false);
  const pendingStreamCameraRef = useRef(null);

  const handleFullscreen = () => {
    const el = videoZoneRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen();
    else document.exitFullscreen();
  };

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('en-GB', { hour12: false }));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);


  useEffect(() => {
    if (!isStreaming || !hlsRef.current || !videoRef.current) return;
    const checkStreamHealth = () => {
      const video = videoRef.current;
      const hls = hlsRef.current;
      if (!video || !hls) return;
      if (video.readyState === 0 || (video.currentTime === 0 && !video.paused && video.duration > 0)) {
        console.warn('[Health] Stream appears stalled, attempting recovery...');
        if (selectedCamera && cameraDetail) {
          setTimeout(() => {
            startHlsStream(selectedCamera.id);
          }, 2000);
        }
      }
    };

    const healthCheckInterval = setInterval(checkStreamHealth, 30000);
    return () => clearInterval(healthCheckInterval);
  }, [isStreaming, selectedCamera, cameraDetail]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (selectedCamera) {
          sessionStorage.setItem('lastSelectedCamera', JSON.stringify({
            id: selectedCamera.id,
            wasStreaming: isStreaming,
            timestamp: Date.now()
          }));
        }
        setTimeout(() => {
          if (selectedCamera && cameraDetail && cameraDetail.status === 'active') {
            if (!hlsRef.current || !videoRef.current || videoRef.current.readyState === 0) {
              stopHlsStream();
              streamStoppedRef.current = false;
              streamRunningRef.current = false;
              hlsStartingRef.current = false;
              startHlsStream(selectedCamera.id);
            } else if (videoRef.current.paused) {
              videoRef.current.play().catch(err => {
                console.warn('[Visibility] Failed to resume video:', err.message);
              });
            }
          }
        }, 1000);
      } else {
        if (videoRef.current && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    };

    const handleBeforeUnload = (e) => {
      if (selectedCamera) {
        sessionStorage.setItem('lastSelectedCamera', JSON.stringify({
          id: selectedCamera.id,
          wasStreaming: isStreaming,
          timestamp: Date.now()
        }));
      }
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch (err) {
          console.warn('[Unload] Error destroying HLS:', err.message);
        }
        hlsRef.current = null;
      }
      if (cameraWsRef.current) {
        cameraWsRef.current.close();
        cameraWsRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [selectedCamera, cameraDetail, isStreaming]);


  const getWsHost = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = new URL(getAPI('cameraData')).host;
    return `${protocol}//${host}`;
  };

  const drawBoundingBox = ({ bounding_box: bb, object_type, confidence }) => {
    const canvas = canvasRef.current;
    if (!canvas || !bb || bb.x1 == null) return;
    requestAnimationFrame(() => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#fbbf24';
      ctx.fillStyle = '#fbbf24';
      ctx.strokeRect(bb.x1, bb.y1, bb.x2 - bb.x1, bb.y2 - bb.y1);
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText(`${object_type} ${(confidence * 100).toFixed(0)}%`, bb.x1, bb.y1 - 5);
      setTimeout(() => {
        if (canvas && ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }, 3000);
    });
  };

  const connectCameraWs = (cameraId, attempt = 0) => {
    if (cameraWsRef.current) {
      cameraWsRef.current.onclose = null;
      cameraWsRef.current.close();
      cameraWsRef.current = null;
    }
    wsStoppedRef.current = false;
    const url = `${getWsHost()}/ws/moniter/${user.store}/camera/${cameraId}/`;
    const ws = new WebSocket(url);
    cameraWsRef.current = ws;
    ws.onopen = () => {
      attempt = 0;
    };
    ws.onclose = (e) => {
      cameraWsRef.current = null;
      if (wsStoppedRef.current) return;
      const delay = Math.min(1000 * 2 ** attempt, 30000);
      wsReconnectRef.current = setTimeout(() => connectCameraWs(cameraId, attempt + 1), delay);
    };
    ws.onerror = (e) => console.error('[WS] Error:', e);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'detection_alert' && msg.payload?.bounding_box) {
          drawBoundingBox(msg.payload);
        }
      } catch (err) {
        console.error('[WS] Failed to parse message:', err);
      }
    };
  };

  const disconnectCameraWs = () => {
    wsStoppedRef.current = true;
    if (wsReconnectRef.current) { clearTimeout(wsReconnectRef.current); wsReconnectRef.current = null; }
    if (cameraWsRef.current) {
      cameraWsRef.current.onclose = null;
      cameraWsRef.current.close();
      cameraWsRef.current = null;
    }
  };

  const startHlsStream = async (cameraId) => {
    if (hlsStartingRef.current) {
      console.warn('[HLS] startHlsStream already in progress, skipping duplicate call');
      return;
    }
    hlsStartingRef.current = true;
    streamStoppedRef.current = false;
    streamRunningRef.current = false;
    setStreamConnecting(true);

    const abortController = new AbortController();
    streamAbortRef.current = abortController;
    const signal = abortController.signal;
    const pollForSegments = async (hlsUrl, maxRetries = 15) => {
      for (let i = 0; i < maxRetries; i++) {
        if (signal.aborted) return false;
        try {
          const ctrl = new AbortController();
          const timeout = setTimeout(() => ctrl.abort(), 5000);
          signal.addEventListener('abort', () => ctrl.abort(), { once: true });
          const res = await fetch(hlsUrl, { method: 'GET', signal: ctrl.signal, cache: 'no-cache' });
          clearTimeout(timeout);
          const text = await res.text();
          if (text.includes('#EXTINF')) return true;
        } catch (err) {
          if (signal.aborted) return false;
        }
        await new Promise(r => {
          const t = setTimeout(r, 2000);
          signal.addEventListener('abort', () => { clearTimeout(t); r(); }, { once: true });
        });
      }
      return false;
    };
    try {
      const res = await Get(`${getAPI('cameraData')}${cameraId}/stream-url/`);
      const hlsUrl = res?.hls_url;
      if (!hlsUrl) throw new Error('No HLS URL returned from server');

      const ready = await pollForSegments(hlsUrl);
      if (signal.aborted) return;
      if (!ready) throw new Error('Stream segments not ready after waiting');
      if (hlsRef.current) {
        try {
          hlsRef.current.destroy();
        } catch (err) {
          console.warn('[HLS] Error destroying existing HLS:', err.message);
        }
        hlsRef.current = null;
      }
      if (!videoRef.current) {
        console.error('[HLS] Video element not found');
        throw new Error('Video element not available');
      }


      const video = videoRef.current;
      video.src = '';
      video.srcObject = null;
      video.load();
      await new Promise(resolve => setTimeout(resolve, 500));
      if (Hls.isSupported()) {
        const hls = new Hls({
          lowLatencyMode: true,
          manifestLoadingMaxRetry: 3,
          levelLoadingMaxRetry: 3,
          fragLoadingMaxRetry: 3,
          manifestLoadingTimeOut: 10000,
          levelLoadingTimeOut: 10000,
          fragLoadingTimeOut: 20000,
          enableWorker: true,
          startLevel: -1,
          capLevelToPlayerSize: true,
          debug: false
        });
        hlsRef.current = hls;
        if (!window.hlsTimeouts) window.hlsTimeouts = [];
        hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
          if (videoRef.current) {
            videoRef.current.play().catch(err => {
              console.warn('[HLS] Autoplay failed:', err.message);
            });
          }
        });
        hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
        });
        hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
        });
        hls.on(Hls.Events.ERROR, (event, data) => {
          console.error('[HLS] Error details:', {
            type: data.type,
            details: data.details,
            fatal: data.fatal,
            reason: data.reason,
            response: data.response,
            url: data.url
          });
          if (data.fatal) {
            console.error('[HLS] Fatal error detected, attempting recovery...');
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                const networkTimeout = setTimeout(() => {
                  if (hlsRef.current) {
                    hlsRef.current.startLoad();
                  }
                }, 1000);
                window.hlsTimeouts.push(networkTimeout);
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                const mediaTimeout = setTimeout(() => {
                  if (hlsRef.current) {
                    hlsRef.current.recoverMediaError();
                  }
                }, 1000);
                window.hlsTimeouts.push(mediaTimeout);
                break;
              default:
                if (data.details === 'attachMediaError') {
                  handleToast({ type: 'ERROR', message: 'Failed to attach video stream.' });
                } else {
                  hls.destroy();
                  hlsRef.current = null;
                  handleToast({ type: 'ERROR', message: 'Stream failed. Please check camera connection.' });
                }
                break;
            }
          } else {
            console.warn('[HLS] Non-fatal error:', data.details);
          }
        });
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        });
        hls.on(Hls.Events.MEDIA_DETACHED, () => {
        });
        hls.on(Hls.Events.DESTROYING, () => {
        });
        hls.loadSource(hlsUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = hlsUrl;
        video.play().catch(err => {
          console.warn(' [HLS] Native autoplay failed:', err.message);
        });
      } else {
        console.error('[HLS] HLS not supported in this browser');
        throw new Error('HLS not supported in this browser');
      }
      hlsStartingRef.current = false;
      setStreamConnecting(false);
    } catch (err) {
      console.error('[HLS] Failed to start stream:', err);
      hlsStartingRef.current = false;
      setStreamConnecting(false);
      if (!signal.aborted) {
        handleToast({ type: 'ERROR', message: 'Failed to load stream. Please check camera connection.' });
      }
    }
  };


  const stopHlsStream = () => {
    streamStoppedRef.current = true;
    streamRunningRef.current = false;
    hlsStartingRef.current = false;
    setStreamConnecting(false);
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    if (window.hlsTimeouts) {
      window.hlsTimeouts.forEach(id => clearTimeout(id));
      window.hlsTimeouts = [];
    }
    if (hlsRef.current) {
      try {
        const hls = hlsRef.current;
        hls.off(Hls.Events.MANIFEST_PARSED);
        hls.off(Hls.Events.LEVEL_LOADED);
        hls.off(Hls.Events.FRAG_LOADED);
        hls.off(Hls.Events.ERROR);
        hls.off(Hls.Events.MEDIA_ATTACHED);
        hls.off(Hls.Events.MEDIA_DETACHED);
        hls.off(Hls.Events.DESTROYING);
        hls.destroy();
      } catch (err) {
        console.warn('[HLS] Error during HLS destroy:', err.message);
      }
      hlsRef.current = null;
    }
    if (videoRef.current) {
      const video = videoRef.current;
      video.onloadstart = null;
      video.onloadeddata = null;
      video.oncanplay = null;
      video.onplay = null;
      video.onpause = null;
      video.onerror = null;
      video.onended = null;
      video.pause();
      video.src = '';
      video.srcObject = null;
      video.load();
    }
  };

  // const handleManualReconnect = async () => {
  //   if (!selectedCamera) return;
  //   const camId = cameraDetail?.id || selectedCamera.id;
  //   if (streamAbortRef.current) { streamAbortRef.current.abort(); streamAbortRef.current = null; }
  //   if (window.hlsTimeouts) { window.hlsTimeouts.forEach(id => clearTimeout(id)); window.hlsTimeouts = []; }

  //   stopHlsStream();
  //   disconnectCameraWs();
  //   setIsStreaming(false);
  //   try { await Post(`${getAPI('cameraData')}${camId}/stop-stream/`, {}); } catch { }

  //   try {
  //     const response = await Post(`${getAPI('cameraData')}${camId}/start-stream/`, {});
  //     setIsStreaming(true);
  //     connectCameraWs(camId);
  //     streamStoppedRef.current = false;
  //     streamRunningRef.current = false;
  //     startHlsStream(camId);
  //   } catch (err) {
  //     console.error('[Manual] Reconnect failed:', err);
  //     handleToast({ type: 'ERROR', message: 'Reconnect failed.' });
  //   }
  // };
  
  
  useEffect(() => {
    if (detailLoading) return;
    if (!pendingStreamCameraRef.current) return;
    if (!videoRef.current) return;
    const id = pendingStreamCameraRef.current;
    pendingStreamCameraRef.current = null;
    streamStoppedRef.current = false;
    streamRunningRef.current = false;
    startHlsStream(id);
  }, [detailLoading]);

  useEffect(() => {
    const lastSession = sessionStorage.getItem('lastSelectedCamera');
    let recoveryCamera = null;

    if (lastSession) {
      try {
        const sessionData = JSON.parse(lastSession);
        if (Date.now() - sessionData.timestamp < 300000) {
          recoveryCamera = sessionData;
        } else {
          sessionStorage.removeItem('lastSelectedCamera');
        }
      } catch (err) {
        console.warn('[Init] Failed to parse session data:', err.message);
        sessionStorage.removeItem('lastSelectedCamera');
      }
    }
    Get('cameraData')
      .then(data => {
        const list = Array.isArray(data) ? data : data?.results || [];
        setCameras(list);
        if (recoveryCamera) {
          const cameraToRecover = list.find(c => c.id === recoveryCamera.id);
          if (cameraToRecover) {
            sessionStorage.removeItem('lastSelectedCamera');
            setSelectedCamera(cameraToRecover);
            fetchCameraDetail(cameraToRecover.id);
            return;
          }
        }
        const activeCamera = list.find(c => c.status === 'active');
        if (activeCamera) {
          setSelectedCamera(activeCamera);
          fetchCameraDetail(activeCamera.id);
        }
      })
      .catch(err => {
        console.error('[Init] Failed to load cameras:', err);
        setCameras([]);
      });

    return () => {
      pendingStreamCameraRef.current = null;
      stopHlsStream();
      disconnectCameraWs();
      if (videoRef.current) {
        videoRef.current.src = '';
        videoRef.current.srcObject = null;
        videoRef.current.load();
      }
    };
  }, []);


  const fetchCameraDetail = async (id) => {
    setDetailLoading(true);
    setCameraDetail(null);
    setIsStreaming(false);
    pendingStreamCameraRef.current = null;
    stopHlsStream();
    disconnectCameraWs();
    try {
      const data = await Get(`${getAPI('cameraData')}${id}/`);
      setCameraDetail(data);
      if (data.status === 'active') {
        setIsStreaming(true);
        connectCameraWs(id);
        pendingStreamCameraRef.current = id;
      }
    } catch (err) {
      console.error('[Camera] Failed to fetch details:', err);
      handleToast({ type: 'ERROR', message: 'Failed to fetch camera details. Check BE connection.' });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectCamera = (cam) => {
    if (selectedCamera?.id === cam.id) {
      return;
    }
    sessionStorage.setItem('lastSelectedCamera', JSON.stringify({
      id: cam.id,
      wasStreaming: isStreaming,
      timestamp: Date.now()
    }));
    setDetailLoading(true);
    setSelectedCamera(cam);
    setTimeout(() => {
      fetchCameraDetail(cam.id);
    }, 100);
  };

  const handleStartStream = async () => {
    if (!cameraDetail) {
      console.warn('[Stream] No camera detail available');
      return;
    }
    const camId = cameraDetail.id;
    setStreamLoading(true);
    try {
      const response = await Post(`${getAPI('cameraData')}${camId}/start-stream/`, {});
      setIsStreaming(true);
      connectCameraWs(camId);
      streamStoppedRef.current = false;
      streamRunningRef.current = false;
      startHlsStream(camId);
      setCameras(prev => prev.map(c => c.id === selectedCamera.id ? { ...c, status: 'active' } : c));
      handleToast({ type: 'SUCCESS', message: response?.data?.detail || 'Stream started.' });
    } catch (err) {
      console.error('[Stream] Failed to start:', err);
      handleToast({ type: 'ERROR', message: 'Failed to start stream.' });
    } finally {
      setStreamLoading(false);
    }
  };

  const handleStopStream = async () => {
    const camId = cameraDetail?.id || selectedCamera?.id;
    if (!camId) return;
    if (streamAbortRef.current) { streamAbortRef.current.abort(); streamAbortRef.current = null; }
    if (window.hlsTimeouts) { window.hlsTimeouts.forEach(id => clearTimeout(id)); window.hlsTimeouts = []; }

    try {
      const response = await Post(`${getAPI('cameraData')}${camId}/stop-stream/`, {});
      handleToast({ type: 'SUCCESS', message: 'Stream stopped.' });
    } catch (err) {
      console.error('[Stream] Failed to stop:', err);
      handleToast({ type: 'ERROR', message: 'Failed to stop stream.' });
    } finally {
      stopHlsStream();
      disconnectCameraWs();
      setIsStreaming(false);
      setDetailLoading(false);
      setCameraDetail(prev => prev ? { ...prev, status: 'inactive', is_recording: false } : null);
      setCameras(prev => prev.map(c => c.id === camId ? { ...c, status: 'inactive' } : c));
    }
  };

  const activeCams = cameras.filter(c => c.status === 'active').length;
  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

  return (
    <>
      <div className="monitor-wrap responsive-box">
        <div className="monitor-content">
          <div className="monitor-topbar">
            <div className="monitor-brand">
              <div className="monitor-brand-mark">
                <svg className="monitor-brand-svg" viewBox="0 0 24 24">
                  <path d="M17 10.5V7a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h12a1 1 0 001-1v-3.5l4 4v-11l-4 4z" />
                </svg>
              </div>
              <div>
                <div className="monitor-brand-name">Live Monitor</div>
                <div className="monitor-brand-sub">▸ SURVEILLANCE SYSTEM</div>
              </div>
            </div>
            <div className="monitor-topbar-right">
              <div className="monitor-sys-pill">
                <span className="monitor-sys-dot" />
                SYS ONLINE · {activeCams}/{cameras.length} ACTIVE
              </div>
              <div className="monitor-clock">
                <div className="monitor-clock-time">{clock}</div>
                <div className="monitor-clock-date">{dateStr}</div>
              </div>
            </div>
          </div>
          <div className="monitor-layout">
            <div className="monitor-sidebar">
              <div className="monitor-sec-label">Cameras ({cameras.length})</div>
              <div className="monitor-cam-list">
                {cameras.length === 0 ? (
                  <div className="monitor-no-cams">// NO FEEDS FOUND</div>
                ) : cameras.map((cam, i) => {
                  const s = getStatus(cam.status);
                  const isSelected = selectedCamera?.id === cam.id;
                  return (
                    <div
                      key={cam.id}
                      className={`monitor-cam-row${isSelected ? ' sel' : ''}`}
                      onClick={() => handleSelectCamera(cam)}
                    >
                      <div className="monitor-cam-thumb">
                        📷
                        <span className="monitor-cam-thumb-dot" style={{ background: s.dot }} />
                      </div>
                      <div className="monitor-cam-meta">
                        <div className="monitor-cam-name">{cam.name}</div>
                        <div className="monitor-cam-loc">{cam.location}</div>
                      </div>
                      <span className="monitor-cam-badge" style={{ background: s.bg, color: s.text }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
                        {cam.status}
                      </span>
                      {/* <span className="monitor-cam-idx">CAM-{String(i + 1).padStart(2, '0')}</span> */}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="monitor-main">
              {!selectedCamera ? (
                <div className="monitor-empty-panel">
                  <div className="monitor-empty-ring">📡</div>
                  <div className="monitor-empty-label">No feed selected</div>
                  <div className="monitor-empty-hint">← pick a camera from the list</div>
                </div>
              ) : detailLoading ? (
                <div className="monitor-empty-panel">
                  <div className="monitor-empty-ring monitor-loading-ring">📷</div>
                  <div className="monitor-empty-label">Connecting to feed…</div>
                  <div className="monitor-empty-hint">please wait</div>
                  <button className="monitor-btn-stop" style={{ marginTop: '16px' }} onClick={handleStopStream}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M6 6h12v12H6z" /></svg>
                    STOP STREAM
                  </button>
                </div>
              ) : cameraDetail ? (
                <div className="monitor-panel">
                  <div className="monitor-panel-hdr">
                    <div>
                      <div className="monitor-panel-title">{cameraDetail.name}</div>
                      <div className="monitor-panel-sub">{cameraDetail.group_name} · {cameraDetail.location}</div>
                    </div>
                    {isStreaming ? (
                      <div className="monitor-live-tag" style={{ background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)', color: '#dc2626' }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#dc2626', display: 'inline-block', animation: 'monitorLivePulse 1s infinite' }} />
                        LIVE
                      </div>
                    ) : (
                      <div className="monitor-live-tag" style={{ background: 'rgba(148,163,184,0.12)', border: '1px solid rgba(148,163,184,0.35)', color: '#94a3b8' }}>
                        <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#94a3b8', display: 'inline-block' }} />
                        OFFLINE
                      </div>
                    )}
                  </div>

                  <div className="monitor-video-zone" ref={videoZoneRef}>
                    <span className="monitor-corner tl" />
                    <span className="monitor-corner tr" />
                    <span className="monitor-corner bl" />
                    <span className="monitor-corner br" />
                    {isStreaming && <div className="monitor-sweep" />}
                    <video
                      ref={videoRef}
                      autoPlay playsInline muted
                      style={{ width: '100%', maxHeight: '380px', objectFit: 'contain', display: isStreaming ? 'block' : 'none' }}
                    />
                    <canvas
                      ref={canvasRef}
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', display: isStreaming ? 'block' : 'none' }}
                    />
                    {isStreaming && (
                      <button className="monitor-fullscreen-btn" onClick={handleFullscreen} title="Fullscreen">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                        </svg>
                      </button>
                    )}
                    {!isStreaming && !streamConnecting && (
                      <div className="monitor-offline">
                        <div className="monitor-offline-ring">📷</div>
                        <div className="monitor-offline-label">No Signal</div>
                        <div className="monitor-offline-hint">// press start stream to activate</div>
                      </div>
                    )}
                    {streamConnecting && (
                      <div className="monitor-offline">
                        <div className="monitor-offline-ring">📡</div>
                        <div className="monitor-offline-label">Connecting stream…</div>
                        <div className="monitor-offline-hint">// waiting for segments</div>
                      </div>
                    )}
                  </div>

                  <div className="monitor-stats">
                    {[
                      { label: 'Frame Rate', value: `${cameraDetail.fps} FPS`, rec: false },
                      { label: 'Resolution', value: `${cameraDetail.resolution_width}×${cameraDetail.resolution_height}`, rec: false },
                      { label: 'Recording', value: cameraDetail.is_recording ? '● REC' : '○ IDLE', rec: cameraDetail.is_recording },
                    ].map(({ label, value, rec }) => (
                      <div key={label} className="monitor-stat">
                        <div className="monitor-stat-lbl">{label}</div>
                        <div className={`monitor-stat-val${rec ? ' rec' : ''}`}>{value}</div>
                      </div>
                    ))}
                  </div>


                  <div className="monitor-action-bar">
                    {!isStreaming && !streamLoading ? (
                      <button className="monitor-btn-start" onClick={handleStartStream}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        START STREAM
                      </button>
                    ) : (
                      <>
                        <button className="monitor-btn-stop" onClick={handleStopStream}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 6h12v12H6z" />
                          </svg>
                          STOP STREAM
                        </button>
                        {/* <button
                          className="monitor-btn-reconnect"
                          onClick={handleManualReconnect}
                          style={{
                            marginLeft: '10px',
                            padding: '8px 16px',
                            background: 'rgba(59,130,246,0.1)',
                            border: '1px solid rgba(59,130,246,0.3)',
                            color: '#3b82f6',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '4px' }}>
                            <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                          </svg>
                          RECONNECT
                        </button> */}
                      </>
                    )}
                  </div>

                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default CameraStream;
